import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, ArrowRight, Store, Search, Plus, Clock, Loader2, X, Home } from "lucide-react";
import { useAuth } from '../context/AuthContext';
import { saveAccount, getSavedAccounts } from './utils/accountManager';
import { API_URL } from '../api/config';

const AccountSwitch = () => {
  const { user, userToken, updateUserData, signOut, signIn } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [recentAccounts, setRecentAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Unified account loader
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const localAccounts = await getSavedAccounts();
      setAccounts(localAccounts);
      
      const response = await fetch(`${API_URL}/available-outlets`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}` 
        },
        body: JSON.stringify({
          email: user?.email || ''
        })
      });
      
      if (response.ok) {
        const apiData = await response.json();
        if (apiData.success && apiData.outlets) {
          // Merge API accounts with local accounts
          const mergedAccounts = [
            ...apiData.outlets, 
            ...localAccounts.filter(la => !apiData.outlets.some(aa => aa.restaurantId === la.restaurantId))
          ];
          setAccounts(mergedAccounts);
          await Promise.all(mergedAccounts.map(saveAccount));
        }
      }
    } catch (error) {
      console.error('Account loading error:', error);
    } finally {
      setLoading(false);
    }
  }, [userToken, user]);

  useEffect(() => {
    const loadInitialData = async () => {
      const recent = JSON.parse(localStorage.getItem('recentAccounts') || '[]');
      setRecentAccounts(recent);
      await loadAccounts();
    };
    loadInitialData();
  }, [loadAccounts]);

  const handleSwitchAccount = async (account) => {
    try {
      setLoading(true);
      
      // Update recent accounts
      const newRecent = [account, ...recentAccounts.filter(a => a.restaurantId !== account.restaurantId)].slice(0, 3);
      localStorage.setItem('recentAccounts', JSON.stringify(newRecent));
      setRecentAccounts(newRecent);
  
      const currentToken = userToken;
      if (!currentToken) {
        setToast({ 
          show: true, 
          message: 'Authentication token missing. Please log in again.', 
          type: 'error' 
        });
        navigate('/login', { 
          state: { 
            returnUrl: '/account-switch',
            message: 'Your session has expired. Please log in again.' 
          } 
        });
        return;
      }
  
      // Use authenticated email from context or fallback to the account's email.
      const emailToUse = user?.email || account.email || '';
  
      const response = await fetch(`${API_URL}/auto-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          email: emailToUse,
          restaurantId: account.restaurantId
        })
      });
  
      if (response.status === 401) {
        navigate('/login', { 
          state: { 
            returnUrl: '/account-switch',
            accountToSwitch: account,
            message: 'Your session has expired. Please log in again.' 
          } 
        });
        return;
      }
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Login failed with status: ${response.status}`);
      }
  
      const data = await response.json();
      if (!data.success || !data.user) {
        throw new Error('Invalid response format: missing user data');
      }
  
      if (data.token) {
        await signIn({
          token: data.token,
          user: data.user
        });
      } else {
        await updateUserData(data.user);
      }
      
      localStorage.setItem('currentAccount', JSON.stringify(data.user));
      
      // Save account including email to local storage.
      await saveAccount({
        ...account,
        email: emailToUse,
        ...data.user,
        lastUsed: new Date().toISOString()
      });
      
      setToast({ 
        show: true, 
        message: `Switched to ${data.user.restaurantName}`, 
        type: 'success' 
      });
      
      // Auto refresh the window after a short delay.
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error('Account switch error:', error);
      setToast({ 
        show: true, 
        message: `Switch failed: ${error.message}`, 
        type: 'error' 
      });
      if (error.message.includes('token') || error.message.includes('session')) {
        navigate('/login', { 
          state: { 
            returnUrl: '/account-switch',
            message: error.message 
          } 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(acc =>
    [acc.restaurantName, acc.outletName, acc.restaurantId.toString()]
      .some(field => field && field.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl overflow-hidden">
        {/* Loading spinner */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
              <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
              <span className="text-gray-700 font-medium">Loading...</span>
            </div>
          </div>
        )}

        {/* Toast notification */}
        {toast.show && (
          <div className={`fixed top-4 right-4 z-50 p-3 rounded-lg shadow-lg ${
            toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
          } text-white max-w-md animate-fade-in-down`}>
            <div className="flex justify-between items-center">
              <p className="font-medium">{toast.message}</p>
              <button 
                onClick={() => setToast({ ...toast, show: false })}
                className="ml-4 text-white hover:text-gray-200 focus:outline-none"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar */}
          <div className="bg-gray-50 p-5 md:w-72">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-xl font-bold text-gray-800">Account Manager</h1>
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors"
                title="Back to Dashboard"
              >
                <Home size={18} />
              </button>
            </div>
            
            <button 
              onClick={() => navigate('/signup')}
              className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-sm mb-5"
            >
              <Plus size={16} />
              <span className="font-medium">Add New Account</span>
            </button>

            {recentAccounts.length > 0 && (
              <div className="border-t border-gray-200 pt-5 mt-5">
                <h2 className="flex items-center text-sm font-medium text-gray-600 mb-3">
                  <Clock size={14} className="mr-1.5" /> 
                  Recent Accounts
                </h2>
                <div className="space-y-2">
                  {recentAccounts.map(acc => (
                    <AccountItem 
                      key={`recent-${acc.restaurantId}`} 
                      account={acc} 
                      onClick={handleSwitchAccount} 
                      isRecent={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex-1 p-5">
            <div className="mb-5 flex flex-col md:flex-row md:items-center md:justify-between">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 md:mb-0">All Accounts</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full md:w-64"
                />
              </div>
            </div>

            {filteredAccounts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAccounts.map(acc => (
                  <AccountItem 
                    key={`grid-${acc.restaurantId}`} 
                    account={acc} 
                    onClick={handleSwitchAccount} 
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-lg">
                <Building className="mx-auto h-10 w-10 text-gray-400" />
                <p className="mt-2 text-gray-500">
                  {accounts.length ? "No matching accounts found" : "No accounts found"}
                </p>
                <button 
                  onClick={() => accounts.length ? setSearchTerm('') : navigate('/add-account')}
                  className="mt-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {accounts.length ? 'Clear Search' : 'Add Account'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

const AccountItem = ({ account, onClick, isRecent = false }) => (
  <div 
    onClick={() => onClick(account)}
    className={`
      cursor-pointer transition-all duration-200 
      ${isRecent 
        ? 'flex items-center space-x-3 p-2.5 hover:bg-blue-50 rounded-lg border border-transparent hover:border-blue-100' 
        : 'bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md hover:border-blue-300'
      }
    `}
  >
    {isRecent ? (
      <>
        <div className="flex-shrink-0 bg-blue-100 text-blue-600 p-2 rounded-full">
          <Building size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-gray-800 truncate">{account.restaurantName}</h3>
          <p className="text-xs text-gray-500 truncate">{account.outletName || "Main Outlet"}</p>
        </div>
        <ArrowRight size={14} className="text-gray-400" />
      </>
    ) : (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="bg-blue-100 text-blue-600 p-2 rounded-full">
            <Building size={18} />
          </div>
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
            ID: {account.restaurantId}
          </div>
        </div>
        <div>
          <h3 className="text-base font-medium text-gray-800 mb-1 truncate">{account.restaurantName}</h3>
          <p className="flex items-center text-sm text-gray-500">
            <Store size={14} className="mr-1 flex-shrink-0" /> 
            <span className="truncate">{account.outletName || "Main Outlet"}</span>
          </p>
        </div>
        <div className="pt-2 mt-1 border-t border-gray-100 flex items-center justify-end text-blue-600 group">
          <span className="text-sm mr-1 font-medium group-hover:mr-2 transition-all">Switch</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    )}
  </div>
);

export default AccountSwitch;